import { z } from 'zod';
import { RetentionDaysSchema } from './recordingretention.js';
import { IdSchema, TimestampSchema, MoneySchema } from './common.js';

/**
 * Handsets, dialling lists and cost-centre groupings.
 *
 * A DEVICE is separate from an extension because one extension may be
 * reachable on several devices (desk phone plus mobile app), and a handset gets
 * re-assigned between people without the extension changing.
 */

export const DeviceVendorSchema = z.enum([
  'yealink', 'grandstream', 'fanvil', 'polycom', 'snom', 'cisco', 'generic',
]);

/** What a handset's keys are set up for (docs/26 C2; API modules/provisioning/templates.ts). */
export const ProvisioningRoleSchema = z.enum(['back_office', 'receptionist', 'agent', 'hot_desking']);
export type ProvisioningRole = z.infer<typeof ProvisioningRoleSchema>;

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/** An IPv4 address as a 32-bit number, for the netmask and subnet checks. */
const ipv4ToInt = (ip: string): number =>
  ip.split('.').reduce((n, part) => (n * 256) + Number(part), 0);

/**
 * Network settings written into a handset's configuration (docs/26 C1).
 *
 * Checked here rather than left to the phone, because a phone given a bad
 * static address drops off the network, and a phone off the network cannot
 * fetch the file that would put it right. So: complete, a real netmask, and a
 * gateway the phone can reach from its own address.
 */
export const DeviceNetworkSchema = z.object({
  /** unmanaged writes nothing and leaves the phone's own setting alone. */
  mode: z.enum(['unmanaged', 'dhcp', 'static']),
  ip: z.string().regex(IPV4, 'must be an IPv4 address').nullable().default(null),
  netmask: z.string().regex(IPV4, 'must be an IPv4 netmask').nullable().default(null),
  gateway: z.string().regex(IPV4, 'must be an IPv4 address').nullable().default(null),
  dns: z.array(z.string().regex(IPV4, 'must be an IPv4 address')).max(2).default([]),
  /** A voice VLAN, written whatever the mode. */
  vlan_id: z.number().int().min(1).max(4094).nullable().default(null),
}).strict().superRefine((n, ctx) => {
  if (n.mode !== 'static') return;
  for (const field of ['ip', 'netmask', 'gateway'] as const) {
    if (!n[field]) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'required for a static address' });
    }
  }
  if (!n.ip || !n.netmask || !n.gateway) return;
  // Ones then zeros: 255.255.255.0 is a netmask, 255.0.255.0 is a typo.
  const mask = ipv4ToInt(n.netmask);
  const hosts = ~mask >>> 0;
  if (mask === 0 || ((hosts + 1) & hosts) !== 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['netmask'], message: 'is not a valid netmask' });
    return;
  }
  const subnet = (ip: string) => (ipv4ToInt(ip) & mask) >>> 0;
  if (n.ip === n.gateway) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gateway'], message: "cannot be the handset's own address" });
  } else if (subnet(n.ip) !== subnet(n.gateway)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gateway'], message: 'is not on the same subnet as the address' });
  }
});
export type DeviceNetwork = z.infer<typeof DeviceNetworkSchema>;

export const DeviceLineSchema = z.object({
  id: IdSchema,
  extension_id: IdSchema,
  extension_number: z.string(),
  extension_name: z.string(),
  line_number: z.number().int(),
  label: z.string().nullable(),
});

export const DeviceSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  mac_address: z.string().nullable(),
  vendor: DeviceVendorSchema,
  model: z.string().nullable(),
  label: z.string().nullable(),
  firmware_target: z.string().nullable(),
  status: z.enum(['unprovisioned', 'provisioned', 'online', 'offline', 'retired']),
  /** False means the handset is excluded from its extension's ring. */
  ring_enabled: z.boolean().optional(),
  /** Accepts hot desk logins (*31). A property of the handset, not of a person. */
  hot_desk_enabled: z.boolean().optional(),
  /** The catalogue entry (docs/26 C1). Null for a handset that is not listed. */
  handset_model_id: z.string().nullable().optional(),
  provisioning_role: ProvisioningRoleSchema.optional(),
  network: DeviceNetworkSchema.optional(),
  /** The handset's own SIP identity; null means it shares the extension's. */
  sip_username: z.string().nullable().optional(),
  /** The template its keys come from (docs/29); none renders the Back office layout. */
  template_id: IdSchema.nullable().optional(),
  template: z.object({
    id: IdSchema,
    slug: z.string(),
    name: z.string(),
    version: z.number().int(),
    /** Null for a platform template. */
    tenant_id: IdSchema.nullable(),
    /** Set when it is one extension's own copy. */
    extension_id: IdSchema.nullable(),
  }).nullable().optional(),
  /** The template version the phone last fetched: behind when lower than the template's. */
  template_version_served: z.number().int().nullable().optional(),
  /** The model the phone named in its last fetch. */
  reported_model: z.string().nullable().optional(),
  /** Whether the phone has its own provisioning login yet: set on its first fetch. */
  provisioning_login: z.boolean().optional(),
  provisioned_at: TimestampSchema.nullable(),
  last_seen_at: TimestampSchema.nullable(),
  /** When the provisioning window closes. Null means shut. */
  provisioning_open_until: TimestampSchema.nullable().optional(),
  /**
   * Whether the handset has pinned the address it provisions from. Once it has,
   * it can re-fetch on every reboot without anybody opening a window.
   */
  address_pinned: z.boolean().optional(),
  lines: z.array(DeviceLineSchema).default([]),
  created_at: TimestampSchema,
});
export type Device = z.infer<typeof DeviceSchema>;

/**
 * MAC accepted in any common notation. The database column normalises it, so
 * 'AA:BB:CC:DD:EE:FF' and 'aabbccddeeff' cannot both exist as separate rows for
 * one physical phone.
 */
const MAC_RE = /^(?:[0-9A-Fa-f]{2}[:-]?){5}[0-9A-Fa-f]{2}$/;
export const MacAddressSchema = z.string().regex(MAC_RE, 'must be a 12-digit MAC address');

export const DeviceUpsertSchema = z.object({
  mac_address: MacAddressSchema.nullable().optional(),
  vendor: DeviceVendorSchema.default('generic'),
  model: z.string().max(64).nullable().optional(),
  label: z.string().max(120).nullable().optional(),
  site_id: IdSchema.nullable().optional(),
  /**
   * Whether this handset rings.
   *
   * Off excludes it from the extension's fork BEFORE the call is offered --
   * unlike a phone's own do-not-disturb button, which stops it alerting but
   * still lets the call be forked to it and still holds a channel.
   */
  ring_enabled: z.boolean().default(true),
  /**
   * The handset, from the catalogue. When given, vendor and model are the
   * catalogue's and whatever else was sent for them is ignored.
   */
  handset_model_id: z.string().regex(/^[a-z0-9][a-z0-9-]{1,40}$/).nullable().optional(),
  provisioning_role: ProvisioningRoleSchema.optional(),
  /** The provisioning template its keys come from (docs/29). */
  template_id: IdSchema.nullable().optional(),
  network: DeviceNetworkSchema.optional(),
  lines: z.array(z.object({
    extension_id: IdSchema,
    line_number: z.number().int().min(1).max(24).default(1),
    label: z.string().max(64).optional(),
  })).default([]),
}).strict();

/**
 * Updating a device, where ABSENT means "leave it alone".
 *
 * The create schema defaults `lines` to an empty array, which is right when
 * creating and destructive when patching: sending only `ring_enabled` would
 * default lines to [] and unassign every extension on the handset. That is not
 * hypothetical -- it is exactly what happened the first time a device field was
 * toggled on its own.
 */
export const DeviceUpdateSchema = DeviceUpsertSchema.partial().strict();

export const AdminGroupSchema = z.object({
  id: IdSchema,
  name: z.string(),
  code: z.string().nullable(),
  extension_count: z.number().int().optional(),
});

export const AdminGroupUpsertSchema = z.object({
  name: z.string().min(1).max(80),
  code: z.string().max(24).nullable().optional(),
}).strict();

export const VoicemailBoxSchema = z.object({
  id: IdSchema,
  extension_id: IdSchema.nullable(),
  mailbox_number: z.string(),
  name: z.string().nullable(),
  email_to: z.array(z.string()).nullable(),
  email_attach: z.boolean(),
  delete_after_email: z.boolean(),
  transcribe: z.boolean(),
  max_message_sec: z.number().int(),
  greeting_mode: z.enum(['default', 'recorded', 'tts']),
  greeting_text: z.string().nullable(),
  has_pin: z.boolean(),
});
export type VoicemailBox = z.infer<typeof VoicemailBoxSchema>;

export const VoicemailUpsertSchema = z.object({
  mailbox_number: z.string().regex(/^\d{2,6}$/),
  name: z.string().max(120).nullable().optional(),
  email_to: z.array(z.string().email()).max(10).default([]),
  email_attach: z.boolean().default(true),
  delete_after_email: z.boolean().default(false),
  transcribe: z.boolean().default(false),
  max_message_sec: z.number().int().min(30).max(1800).default(300),
  greeting_mode: z.enum(['default', 'recorded', 'tts']).default('default'),
  greeting_text: z.string().max(600).nullable().optional(),
  /** Cleared by sending null; never returned. */
  pin: z.string().regex(/^\d{4,10}$/).nullable().optional(),
}).strict();

export const BudgetPeriodSchema = z.enum(['daily', 'weekly', 'monthly']);
/**
 * An extension records every call, or none (owner, 25 September 2026, migration 0118). "Inbound
 * only", "outbound only" and "on demand" were retired with the in-call keys: nobody used them,
 * and recording is not something a person turns on and off during a call.
 */
export const RecordModeSchema = z.enum(['none', 'all']);
export const PhoneEngineSchema = z.enum(['external', 'webrtc', 'mobile', 'softphone']);
export const BudgetAmountSchema = MoneySchema;

/**
 * Account-wide recording policy.
 *
 * `default_mode` is what a NEW extension inherits; it never rewrites existing
 * seats. Retention is the part with legal weight (doc 22) and applies to the
 * whole account, because a per-seat retention would make "delete everything
 * older than N" unanswerable.
 */
export const RecordingPolicySchema = z.object({
  tenant_id: IdSchema,
  default_mode: z.enum(['none', 'all']),
  retention_days: z.number().int().nullable(),
  announce_mode: z.enum(['none', 'inbound', 'outbound', 'both']),
  storage_quota_mb: z.number().int().nullable(),
  updated_at: TimestampSchema.nullable(),
});
export type RecordingPolicy = z.infer<typeof RecordingPolicySchema>;

export const RecordingPolicyUpsertSchema = z.object({
  default_mode: z.enum(['none', 'all']).default('none'),
  retention_days: RetentionDaysSchema.nullable().optional(),
  announce_mode: z.enum(['none', 'inbound', 'outbound', 'both']).default('none'),
  storage_quota_mb: z.number().int().min(1).nullable().optional(),
}).strict();

/** Applies one recording mode to a named set of extensions, in one request. */
export const RecordingBulkSchema = z.object({
  extension_ids: z.array(IdSchema).min(1).max(500),
  record_mode: z.enum(['none', 'all']),
}).strict();
